/** Best-effort server sync for cross-device resume. Local storage stays the
 *  source of truth; failures here are swallowed (offline is expected). */
import { api, ApiError } from '../api/client';
import type { SavedGameOut } from '../api/types';
import type { StoredGame } from './state';

function toPayload(game: StoredGame) {
  return {
    kind: game.kind,
    difficulty: game.difficulty,
    givens: game.givens,
    current: game.current,
    notes: game.notes,
    elapsed_ms: game.elapsedMs,
  };
}

export async function pushGame(game: StoredGame): Promise<void> {
  try {
    await api(`/games/${encodeURIComponent(game.key)}`, {
      method: 'PUT',
      body: toPayload(game),
    });
  } catch (err) {
    // 401 is handled by the client (redirect); anything else is offline/best-effort.
    if (err instanceof ApiError && err.status === 401) throw err;
  }
}

export async function deleteRemote(key: string): Promise<void> {
  try {
    await api(`/games/${encodeURIComponent(key)}`, { method: 'DELETE' });
  } catch {
    /* ignore */
  }
}

export async function pullGames(): Promise<SavedGameOut[]> {
  try {
    return await api<SavedGameOut[]>('/games');
  } catch {
    return [];
  }
}

/** Trailing debounce keyed by game — coalesces rapid moves into one PUT. */
const timers = new Map<string, ReturnType<typeof setTimeout>>();

export function schedulePush(game: StoredGame, delayMs = 1500): void {
  const existing = timers.get(game.key);
  if (existing) clearTimeout(existing);
  timers.set(
    game.key,
    setTimeout(() => {
      timers.delete(game.key);
      void pushGame(game);
    }, delayMs),
  );
}
